import React from "react";
import { AlertTriangle } from "lucide-react";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea, Select } from "@/components/ReportPrimitives";
import {
  buildLotFitReport,
  lotFitChecklist,
  LOT_FIT_QUICK_PHRASES,
} from "@/lib/reports/batch5";
import { lotStore } from "@/lib/reports/storage";
import { useReportRceWiring } from "@/lib/rce/useReportRceWiring";

const emptyState = () => ({
  id: null,
  header: {},
  setup: {},
  data: {},
  obs: {},
  recommendations: {},
  notes: {},
});

const LotFitWarning = ({ testId }) => (
  <div
    data-testid={testId}
    className="card-rs p-3 flex items-start gap-2.5"
    style={{ background: "rgba(244,67,54,0.07)", borderColor: "#C62828" }}
  >
    <AlertTriangle className="w-4 h-4 mt-[2px] shrink-0" style={{ color: "var(--rs-danger)" }} />
    <p className="text-[11px] leading-snug text-[#FCA5A5]">
      <span className="font-semibold" style={{ color: "var(--rs-danger)" }}>Draft record-support only.</span>{" "}
      This tool does NOT certify formation integrity. It does NOT independently classify the test
      outcome as a pass or a fail. It does NOT issue acceptance of the casing shoe. It does NOT
      confirm the integrity status of the formation. It does NOT clear the well for any further
      operations. All interpretation must be performed by competent drilling / well-engineering
      personnel under the approved well program, company procedures, client requirements, and
      applicable regulations.
    </p>
  </div>
);

const Quick = ({ onPick, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {LOT_FIT_QUICK_PHRASES.map((p) => (
      <button key={p} type="button" onClick={() => onPick(p)} className="text-[10.5px] px-2 py-1 rounded-md" style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}>+ {p}</button>
    ))}
  </div>
);

export default function LotFitReport() {
  const rce = useReportRceWiring("lotFitReport");
  const sections = [
    { id: "header", badge: "01", title: "Report Header", defaultOpen: true, render: (s, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Report Date"><TextInput type="date" value={s.header.reportDate} onChange={(v) => set("header","reportDate",v)} testId="lot-h-reportDate" /></Field>
        <Field label="Rig Name"><TextInput value={s.header.rigName} onChange={(v) => set("header","rigName",v)} testId="lot-h-rigName" /></Field>
        <Field label="Well Name"><TextInput value={s.header.wellName} onChange={(v) => set("header","wellName",v)} testId="lot-h-wellName" /></Field>
        <Field label="Operator / Client"><TextInput value={s.header.operator} onChange={(v) => set("header","operator",v)} testId="lot-h-operator" /></Field>
        <Field label="Prepared By"><TextInput value={s.header.preparedBy} onChange={(v) => set("header","preparedBy",v)} testId="lot-h-preparedBy" /></Field>
        <Field label="Test Type (user-entered)" helper="Tool does not redefine the test name."><Select value={s.header.testType} onChange={(v) => set("header","testType",v)} options={["LOT", "FIT", "XLOT"]} placeholder="Select test type…" testId="lot-h-testType" /></Field>
      </div>
    )},
    { id: "setup", badge: "02", title: "Test Setup (User-Entered)", render: (s, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Casing Shoe Depth TVD (ft)"><TextInput type="number" value={s.setup.shoeTvd} onChange={(v) => set("setup","shoeTvd",v)} testId="lot-su-shoeTvd" /></Field>
        <Field label="Casing Shoe Depth MD (ft)"><TextInput type="number" value={s.setup.shoeMd} onChange={(v) => set("setup","shoeMd",v)} testId="lot-su-shoeMd" /></Field>
        <Field label="Hole Size Below Shoe"><TextInput value={s.setup.holeSize} onChange={(v) => set("setup","holeSize",v)} testId="lot-su-holeSize" /></Field>
        <Field label="Mud Weight at Test (ppg)"><TextInput type="number" value={s.setup.mudWeightPpg} onChange={(v) => set("setup","mudWeightPpg",v)} testId="lot-su-mudWeightPpg" /></Field>
        <Field label="Mud Type"><TextInput value={s.setup.mudType} onChange={(v) => set("setup","mudType",v)} testId="lot-su-mudType" /></Field>
        <Field label="Cement Top / Last Cement"><TextInput value={s.setup.cementTop} onChange={(v) => set("setup","cementTop",v)} testId="lot-su-cementTop" /></Field>
      </div>
    )},
    { id: "data", badge: "03", title: "Test Procedure (As Recorded)", render: (s, set) => (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Pump Rate (user-entered)"><TextInput value={s.data.pumpRate} onChange={(v) => set("data","pumpRate",v)} testId="lot-d-pumpRate" /></Field>
          <Field label="Max Surface Pressure (psi, user-entered)"><TextInput type="number" value={s.data.maxSurfPsi} onChange={(v) => set("data","maxSurfPsi",v)} testId="lot-d-maxSurfPsi" /></Field>
          <Field label="Leak-Off / Departure Pressure (psi, user-entered, if observed)"><TextInput type="number" value={s.data.leakOffPsi} onChange={(v) => set("data","leakOffPsi",v)} testId="lot-d-leakOffPsi" /></Field>
          <Field label="Volume Pumped"><TextInput value={s.data.volumePumped} onChange={(v) => set("data","volumePumped",v)} testId="lot-d-volumePumped" /></Field>
          <Field label="Hold Period"><TextInput value={s.data.holdPeriod} onChange={(v) => set("data","holdPeriod",v)} testId="lot-d-holdPeriod" /></Field>
        </div>
        <Field label="Pressure-Up Increments (text)"><TextArea value={s.data.increments} onChange={(v) => set("data","increments",v)} rows={3} testId="lot-d-increments" /></Field>
        <Field label="Bleed-Off Behaviour (as observed)"><TextArea value={s.data.bleedOff} onChange={(v) => set("data","bleedOff",v)} rows={2} testId="lot-d-bleedOff" /></Field>
      </>
    )},
    { id: "obs", badge: "04", title: "Observations & Witnesses", render: (s, set, helpers) => (
      <>
        <Field label="Observations"><TextArea value={s.obs.observations} onChange={(v) => set("obs","observations",v)} rows={3} testId="lot-ob-observations" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Client Representative"><TextInput value={s.obs.clientRep} onChange={(v) => set("obs","clientRep",v)} testId="lot-ob-clientRep" /></Field>
          <Field label="Driller / Toolpusher"><TextInput value={s.obs.driller} onChange={(v) => set("obs","driller",v)} testId="lot-ob-driller" /></Field>
          <Field label="Mud Engineer"><TextInput value={s.obs.mudEng} onChange={(v) => set("obs","mudEng",v)} testId="lot-ob-mudEng" /></Field>
        </div>
        <Field label="Other Witnesses"><TextArea value={s.obs.otherWitnesses} onChange={(v) => set("obs","otherWitnesses",v)} rows={2} testId="lot-ob-otherWitnesses" /></Field>
        <Field
          label="User-Entered Status"
          helper="Free-text only. Tool does not classify pass/fail. Wording is preserved verbatim."
        >
          <TextInput value={s.obs.userStatus} onChange={(v) => set("obs","userStatus",v)} testId="lot-ob-userStatus" />
        </Field>
        <Quick onPick={(p) => helpers.setState((st) => ({ ...st, obs: { ...st.obs, observations: ((st.obs.observations || "") + (st.obs.observations && !/\s$/.test(st.obs.observations || "") ? " " : "") + p) } }))} testId="lot-ob-quick-phrases" />
      </>
    )},
    { id: "recommendations", badge: "05", title: "Recommendations / Next Steps", render: (s, set) => (
      <Field label="Recommendations"><TextArea value={s.recommendations.text} onChange={(v) => set("recommendations","text",v)} rows={3} testId="lot-rc-text" /></Field>
    )},
    { id: "notes", badge: "06", title: "Rough Notes", render: (s, set) => (
      <Field label="Rough Notes"><TextArea value={s.notes.roughNotes} onChange={(v) => set("notes","roughNotes",v)} rows={3} testId="lot-n-roughNotes" /></Field>
    )},
  ];
  return (
    <ReportShell
      testIdPrefix="lot"
      reportType="lotFitReport"
      safetyCritical={true}
      title="LOT / FIT Report"
      subtitle="Leak-Off Test / Formation Integrity Test record — record-support only. Interpretation by well engineering."
      brandLine="LOT / FIT Report · Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildLotFitReport}
      checklistFn={lotFitChecklist}
      store={lotStore}
      sections={sections}
      initialState={rce.initialState}
      rceContextStrip={rce.rceContextStrip}
      topNotice={<LotFitWarning testId="lot-special-warning-top" />}
      bottomNotice={<LotFitWarning testId="lot-special-warning-bottom" />}
      filenameFromState={(s) => `RigSentinel_LotFit_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`}
    />
  );
}
